/**
 * Display formatting.
 *
 * ONE RULE runs through this file: a value the CRM did not give us renders
 * as an em dash, never as `0`, `NaN`, `null` or an empty cell. A reseller
 * looking at "$0.00 outstanding" would reasonably conclude they are paid
 * up; "—" says the number is unavailable, which is the truth when an
 * upstream call failed. The BFF already normalises missing values to null
 * (see `apps/server/src/routes/crm.ts`) precisely so this layer can be
 * this blunt.
 */

export const EMPTY = "—";

/** Currency, in the reseller's own trading currency. Falls back to the
 * plain number when the currency code is unknown rather than guessing USD
 * — a euro amount labelled "$" is worse than one labelled nothing. */
export function money(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  if (!currency) {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // An unrecognised ISO code throws rather than degrading, and a throw
    // here would blank a whole table row.
    return `${new Intl.NumberFormat().format(value)} ${currency}`;
  }
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return new Intl.NumberFormat().format(value);
}

/** A date, in the viewer's locale. crm-api sends `YYYY-MM-DD` for date
 * columns and ISO-8601 for timestamps; both parse, and anything that does
 * not is shown verbatim rather than as "Invalid Date". */
export function date(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * A DATE-ONLY value (`YYYY-MM-DD`, no time, no zone), in the viewer's
 * locale — `validUntil`, `endsOn` and the like.
 *
 * `date()` above hands a bare `YYYY-MM-DD` to `new Date(...)`, which the spec
 * parses as UTC MIDNIGHT; `toLocaleDateString` then renders that instant in
 * the viewer's own zone, which is a day EARLIER for anyone west of UTC. A
 * reseller in Los Angeles reading a quote that expires "2026-10-09" would see
 * "Oct 8" — the wrong day, in the wrong direction for a deadline. Parsing the
 * three components straight into a LOCAL date sidesteps the UTC round-trip
 * entirely, because there is no timezone-bearing instant in between.
 *
 * A value that carries a time part is not date-only in the first place — it
 * is one of crm-api's ISO-8601 timestamps, which `date()` already parses
 * correctly — so that case is left to `date()` rather than reimplemented.
 */
export function dateOnly(value: string | null | undefined): string {
  if (!value) return EMPTY;
  if (value.includes("T")) return date(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return EMPTY;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(parsed.getTime())) return EMPTY;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function text(value: string | null | undefined): string {
  return value && value.trim() !== "" ? value : EMPTY;
}

/** Title-cases a CRM status slug (`partially_paid` → `Partially paid`) so
 * the table does not show raw column values. */
export function status(value: string | null | undefined): string {
  if (!value || value.trim() === "") return EMPTY;
  const spaced = value.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
